/** Компонента редактирования значений ObjectProperty связанного с активам
 * 
 * Формирует список доступных активов с учётом их связи с каталогом.
 *
 * Кнопки:
 * - "Удалить ссылку". По кнопке удаляется триплет со ссылкой.
 *   Для работы кнопки в props должна быть передана функция onDeleteLink,
 *   и список ссылок должен быть не пуст.
 * 
 * - "Связать" с объектом. По кнопке "Связать" вызывается onSelectLink.
 *   onSelectLink должна вернуть IRI выбранного объекта.
 *   После этого вызывается onAppendLink, которая создаёт триплет со ссылкой.
 * 
 * - "Создать". По кнопке вызывается onNewLink.
 *   onNewLink должна вернуть IRI созданного объекта.
 *   После этого вызывается onAppendLink, которая создаёт триплет со ссылкой.
 * 
 * Все необходимые параметры и используемые методы передаются в props.
 * 
 * Если одна из функций не передаётся, соответствующая кнопка скрывается.
 * 
 * С использованием react-redux берётся настройка языка, наличие авторизации
 * и включаемые группы.
 */
import React from 'react';
import i18n from "i18next";
import { connect } from 'react-redux';
import { Tooltip, Button, Modal, message } from 'antd';
import {
  LinkOutlined,
  FileAddOutlined,
  DisconnectOutlined
} from '@ant-design/icons';

import { SimpleViewLinkComponent } from '../widgets/ViewLink';

const ButtonGroup = Button.Group;
const { confirm } = Modal;

const isFunction = fn => typeof fn === 'function';

const toPromise = value =>
  value && typeof value.then === 'function'
    ? value
    : Promise.resolve(value);

const getIRI = value => {
  if (!value) return undefined;

  if (typeof value === 'string') {
    const iri = value.trim();
    return iri || undefined;
  }

  if (typeof value.value === 'string') {
    const iri = value.value.trim();
    return iri || undefined;
  }

  if (typeof value.IRI === 'string') {
    const iri = value.IRI.trim();
    return iri || undefined;
  }

  if (typeof value.iri === 'string') {
    const iri = value.iri.trim();
    return iri || undefined;
  }

  if (value.object && typeof value.object.value === 'string') {
    const iri = value.object.value.trim();
    return iri || undefined;
  }

  return undefined;
};

/** Форма редактирования простого ссылочного значения */
class LinkSimpleEditor extends React.Component {

  state = {
    loading: false,
  };

  reload(subject, predicat) {
    const { onReload } = this.props;

    if (!isFunction(onReload)) {
      message.success("Для просмотра изменений перегрузите страницу.");
      return Promise.resolve();
    }

    return toPromise(onReload(subject, predicat));
  }

  appendLink(objectIRI) {
    const {
      subject,
      predicat,
      onAppendLink,
    } = this.props;

    if (!objectIRI) {
      return Promise.reject(new Error("IRI объекта не задан."));
    }

    if (!isFunction(onAppendLink)) {
      return Promise.reject(new Error("Не передана функция onAppendLink."));
    }

    return toPromise(onAppendLink(subject, predicat, objectIRI))
      .then(() => this.reload(subject, predicat));
  }

  /** Связать с существующим объектом */
  linkOperation(e) {
    e.preventDefault();

    const {
      subject,
      predicat,
      values = [],
      factory,
      action,
      onSelectLink,
      onAppendLink,
    } = this.props;

    if (!isFunction(onSelectLink) || !isFunction(onAppendLink)) {
      message.error("Операция связывания недоступна: не переданы необходимые функции.");
      return;
    }

    this.setState({ loading: true });

    toPromise(onSelectLink({
      subject,
      predicat,
      values,
      factory,
    }))
      .then(selected => {
        const selectedIRI = getIRI(selected);

        if (!selectedIRI) {
          throw new Error("Не выбран объект для связывания.");
        }

        if (isFunction(action)) {
          action(e, "link", predicat, selectedIRI);
        }

        return this.appendLink(selectedIRI);
      })
      .then(() => {
        message.success(i18n.t('components.editor.individual.message.ok_link'));
        this.setState({ loading: false });
      })
      .catch(info => {
        console.log("LinkSimpleEditor linkOperation error:", info);
        message.error(i18n.t('components.editor.individual.message.e_link', {
          detail: info && info.message ? info.message : info
        }));
        this.setState({ loading: false });
      });
  }

  /** Создать новый объект и связать с ним */
  newOperation(e) {
    e.preventDefault();

    const {
      subject,
      predicat,
      values = [],
      factory,
      action,
      onNewLink,
      onAppendLink,
    } = this.props;

    if (!isFunction(onNewLink) || !isFunction(onAppendLink)) {
      message.error("Операция создания недоступна: не переданы необходимые функции.");
      return;
    }

    this.setState({ loading: true });

    toPromise(onNewLink({
      subject,
      predicat,
      values,
      factory,
    }))
      .then(created => {
        const createdIRI = getIRI(created);

        if (!createdIRI) {
          throw new Error("IRI созданного объекта не задан.");
        }

        if (isFunction(action)) {
          action(e, "new", predicat, createdIRI);
        }

        return this.appendLink(createdIRI);
      })
      .then(() => {
        message.success(i18n.t('components.editor.individual.message.ok_new'));
        this.setState({ loading: false });
      })
      .catch(info => {
        console.log("LinkSimpleEditor newOperation error:", info);
        message.error(i18n.t('components.editor.individual.message.e_new', {
          detail: info && info.message ? info.message : info
        }));
        this.setState({ loading: false });
      });
  }

  /** Удалить существующую связь */
  unlinkOperation(e, item) {
    e.preventDefault();

    const {
      subject,
      predicat,
      action,
      onDeleteLink,
    } = this.props;

    if (!isFunction(onDeleteLink)) {
      message.error("Операция удаления связи недоступна: не передана функция onDeleteLink.");
      return;
    }

    const objectIRI = getIRI(item);

    if (!objectIRI) {
      message.error("Невозможно удалить связь: не найден IRI объекта.");
      return;
    }

    if (isFunction(action)) {
      action(e, "unlink", predicat, objectIRI);
    }

    confirm({
      title: 'Разорвать связь с выбранным объектом',
      content: objectIRI,
      okText: 'Да',
      okType: 'danger',
      cancelText: 'Нет',
      onOk: () => {
        this.setState({ loading: true });

        return toPromise(onDeleteLink(subject, predicat, objectIRI))
          .then(() => this.reload(subject, predicat))
          .then(() => {
            message.success(i18n.t('components.editor.individual.message.ok_unlink'));
            this.setState({ loading: false });
          })
          .catch(info => {
            console.log("LinkSimpleEditor unlinkOperation error:", info);
            message.error(i18n.t('components.editor.individual.message.e_unlink', {
              detail: info && info.message ? info.message : info
            }));
            this.setState({ loading: false });
          });
      },
      onCancel() {},
    });
  }

  render() {
    console.log(this);
    const {
      lang,
      values = [],
      predicat,
      factory,

      onDeleteLink,
      onSelectLink,
      onAppendLink,
      onNewLink,
    } = this.props;

    const { loading } = this.state;

    const canLink =
      isFunction(onSelectLink) &&
      isFunction(onAppendLink);

    const canCreate =
      isFunction(onNewLink) &&
      isFunction(onAppendLink);

    const canUnlink =
      isFunction(onDeleteLink) &&
      values.length > 0;

    return (
      <span>
        <ButtonGroup>
          {canLink && (
            <Tooltip placement="top" title={"Связать"}>
              <Button
                type="dashed"
                size="small"
                loading={loading}
                icon={<LinkOutlined />}
                onClick={e => this.linkOperation(e)}
              />
            </Tooltip>
          )}

          {canCreate && (
            <Tooltip placement="top" title={"Создать новый экземпляр"}>
              <Button
                type="dashed"
                size="small"
                loading={loading}
                icon={<FileAddOutlined />}
                onClick={e => this.newOperation(e)}
              />
            </Tooltip>
          )}
        </ButtonGroup>

        <br />

        {values.map((item, i) => {
          const objectIRI = getIRI(item);
          const key = objectIRI || i;

          return (
            <span key={key}>
              {canUnlink && (
                <Tooltip placement="top" title={"Разорвать связь"}>
                  <Button
                    type="dashed"
                    size="small"
                    loading={loading}
                    icon={<DisconnectOutlined />}
                    onClick={e => this.unlinkOperation(e, item)}
                  />
                </Tooltip>
              )}

              <SimpleViewLinkComponent
                IRI={objectIRI}
                factory={factory}
                triplet={item}
                lang={lang}
              />

              <br />
            </span>
          );
        })}
      </span>
    );
  }
}

function mapStateToProps(state) {
  const { vset } = state;

  return {
    isSignedIn: vset.isSignedIn,
    lang: vset.lang,
    inGroup: vset.inGroup
  };
}

export default connect(
  mapStateToProps
)(LinkSimpleEditor);